import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader, Users, Target, Heart, MessageSquare, Plus, Save, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

// Add type for analysis results
interface AnalysisResults {
  targetAudience: Array<{
    segment: string;
    description: string;
  }>;
  painPoints: Array<{
    problem: string;
  }>;
  desires: Array<{
    desire: string;
  }>;
  socialProof: Array<{
    testimonial: string;
    author: string;
    theme: string;
  }>;
  [key: string]: any;
}

const AudiencePage = () => {
  const [results, setResults] = useState<AnalysisResults | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { id: projectId } = useParams();
  const { toast } = useToast();
  
  // Add state to track if there are unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // State to store the last crawl data for saving
  const [lastCrawlData, setLastCrawlData] = useState<{id: string, url: string, user_id: string} | null>(null);

  // State for adding new items
  const [newAudienceSegment, setNewAudienceSegment] = useState({ segment: "", description: "" });
  const [newPainPoint, setNewPainPoint] = useState({ problem: "" });
  const [newDesire, setNewDesire] = useState({ desire: "" });
  const [newSocialProof, setNewSocialProof] = useState({ testimonial: "", author: "", theme: "" });
  
  // State for showing add forms
  const [showAddAudience, setShowAddAudience] = useState(false);
  const [showAddPainPoint, setShowAddPainPoint] = useState(false);
  const [showAddDesire, setShowAddDesire] = useState(false);
  const [showAddSocialProof, setShowAddSocialProof] = useState(false);

  useEffect(() => {
    const fetchResults = async () => {
      if (!projectId) return;
      setIsLoading(true);

      try {
        const { data: crawl, error } = await supabase
          .from('website_crawls')
          .select('id, analysis_results, url, user_id')
          .eq('project_id', projectId)
          .eq('status', 'completed')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) {
          console.error('Error fetching results:', error);
          throw error;
        }

        if (crawl) {
          // Store the crawl data for saving
          setLastCrawlData({
            id: crawl.id,
            url: crawl.url,
            user_id: crawl.user_id
          });

          if (crawl.analysis_results) {
            // Clean XML tags from the results
            const cleanXMLTags = (text: string) => {
              return text.replace(/<\/?[^>]+(>|$)/g, "");
            };

            // Handle both string and object formats for analysis_results
            let analysisResults: any;
            
            try {
              // If the analysis_results is a string, parse it
              if (typeof crawl.analysis_results === 'string') {
                analysisResults = JSON.parse(crawl.analysis_results);
                console.log('Successfully parsed string analysis_results');
              } else {
                // Otherwise, use it directly as an object
                analysisResults = crawl.analysis_results;
                console.log('Using object analysis_results directly');
              }
              
              console.log('Analysis data keys:', Object.keys(analysisResults));
              
              // Helper function to ensure we're working with arrays
              const ensureArray = (possibleArray: any): any[] => {
                return Array.isArray(possibleArray) ? possibleArray : [];
              };
              
              const cleanedResults: AnalysisResults = {
                targetAudience: ensureArray(analysisResults.targetAudience).map((t: any) => ({
                  ...t,
                  segment: cleanXMLTags(t.segment || ''),
                  description: cleanXMLTags(t.description || '')
                })) || [],
                painPoints: ensureArray(analysisResults.painPoints)
                  .filter((p: any) => p && typeof p === 'object' && p.problem?.trim())
                  .map((p: any) => ({
                    problem: cleanXMLTags(p.problem || '')
                  })) || [],
                desires: ensureArray(analysisResults.desires)
                  .filter((d: any) => d && typeof d === 'object' && d.desire?.trim())
                  .map((d: any) => ({
                    desire: cleanXMLTags(d.desire || '')
                  })) || [],
                socialProof: ensureArray(analysisResults.socialProof)
                  .filter((s: any) => s && typeof s === 'object')
                  .map((s: any) => ({
                    ...s,
                    testimonial: cleanXMLTags(s.testimonial || ''),
                    author: cleanXMLTags(s.author || ''),
                    theme: cleanXMLTags(s.theme || '')
                  })) || []
              };
              
              setResults(cleanedResults);
            } catch (parseError) {
              console.error('Error parsing analysis results:', parseError);
              // Provide empty data structure on error
              setResults({
                targetAudience: [],
                painPoints: [],
                desires: [],
                socialProof: []
              });
              toast({
                title: "Data Processing Error",
                description: "There was an error processing the analysis data",
                variant: "destructive",
              });
            }
          } else {
            setResults({
              targetAudience: [],
              painPoints: [],
              desires: [],
              socialProof: []
            });
          }
        } else {
          // No crawl data
          setResults({
            targetAudience: [],
            painPoints: [],
            desires: [],
            socialProof: []
          });
        }
      } catch (error) {
        console.error('Error fetching results:', error);
        toast({
          title: "Error",
          description: "Failed to fetch audience analysis",
          variant: "destructive",
        });
        setResults({
          targetAudience: [],
          painPoints: [],
          desires: [],
          socialProof: []
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchResults();
  }, [projectId, toast]);

  // Add auto-save effect
  useEffect(() => {
    // Cleanup function that runs when component unmounts or user navigates away
    return () => {
      if (hasUnsavedChanges && projectId && results && lastCrawlData) {
        // Use a synchronous save to ensure it completes before navigation
        saveChangesSync();
      }
    };
  }, [hasUnsavedChanges, projectId, results, lastCrawlData]);

  // Add synchronous save function for cleanup
  const saveChangesSync = async () => {
    if (!projectId || !results || !lastCrawlData) return;
    
    try {
      // We already have the last crawl data, so no need to fetch it again
      const updatedResults = {
        ...(lastCrawlData as any).analysis_results || {},
        targetAudience: results.targetAudience,
        painPoints: results.painPoints,
        desires: results.desires,
        socialProof: results.socialProof
      };
      
      const { error: updateError } = await supabase
        .from('website_crawls')
        .update({ analysis_results: updatedResults })
        .eq('id', lastCrawlData.id);
        
      if (updateError) throw updateError;
    } catch (error) {
      console.error('Error auto-saving changes:', error);
    }
  };

  // Modify the existing saveChanges function to update hasUnsavedChanges
  const saveChanges = async () => {
    if (!projectId || !results) return;
    
    setIsSaving(true);
    try {
      if (!lastCrawlData) {
        toast({
          title: "Error",
          description: "Cannot save without existing analysis data",
          variant: "destructive",
        });
        return;
      }
      
      await saveChangesSync();
      setHasUnsavedChanges(false);
      toast({
        title: "Success",
        description: "Audience analysis saved successfully",
      });
    } catch (error) {
      console.error('Error saving changes:', error);
      toast({
        title: "Error",
        description: "Failed to save changes",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Modify the handlers to set hasUnsavedChanges
  const handleAddAudienceSegment = () => {
    if (!newAudienceSegment.segment.trim() || !newAudienceSegment.description.trim()) return;
    
    if (results) {
      setResults({
        ...results,
        targetAudience: [...results.targetAudience, newAudienceSegment]
      });
      
      setNewAudienceSegment({ segment: "", description: "" });
      setShowAddAudience(false);
      setHasUnsavedChanges(true);
    }
  };

  const handleAddPainPoint = () => {
    if (!newPainPoint.problem.trim() || !results) return;
    
    setResults({
      ...results,
      painPoints: [...results.painPoints, newPainPoint]
    });
    
    setNewPainPoint({ problem: "" });
    setShowAddPainPoint(false);
    setHasUnsavedChanges(true);
  };

  const handleAddDesire = () => {
    if (!newDesire.desire.trim() || !results) return;
    
    setResults({
      ...results,
      desires: [...results.desires, newDesire]
    });
    
    setNewDesire({ desire: "" });
    setShowAddDesire(false);
    setHasUnsavedChanges(true);
  };

  const handleAddSocialProof = () => {
    if (!newSocialProof.testimonial.trim() || !newSocialProof.author.trim() || !results) return;
    
    setResults({
      ...results,
      socialProof: [...results.socialProof, newSocialProof]
    });
    
    setNewSocialProof({ testimonial: "", author: "", theme: "" });
    setShowAddSocialProof(false);
    setHasUnsavedChanges(true);
  };

  const handleRemoveItem = (type: string, index: number) => {
    if (!results) return;
    
    const updatedResults = { ...results };
    (updatedResults[type] as any[]).splice(index, 1);
    setResults(updatedResults);
    setHasUnsavedChanges(true);
  };

  // Add warning when user tries to leave with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <div className="animate-spin">
          <Loader className="h-10 w-10 text-blue-600 dark:text-blue-400" />
        </div>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="container mx-auto p-6">
        <div className="bg-gradient-to-br from-blue-50 to-blue-50 dark:from-blue-950/20 dark:to-blue-950/20 rounded-xl p-8 shadow-md border border-blue-100 dark:border-blue-900/50">
          <div className="text-center py-8">
            <Users className="h-14 w-14 text-blue-500 mx-auto mb-4 opacity-70" />
            <h2 className="text-2xl font-bold text-blue-700 mb-2">No Audience Analysis Available</h2>
            <p className="text-zinc-600 dark:text-zinc-300 max-w-md mx-auto">
              Audience analysis data is not available yet. Please analyze a website first to see audience details.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-white dark:bg-zinc-900 animate-fadeIn">
      {/* Page Header */}
      <div className="border-b border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900">
        <div className="container mx-auto py-6 px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-zinc-900 dark:text-white">Audience Analysis</h1>
              <p className="text-zinc-500 dark:text-zinc-400 mt-1">Detailed analysis of the target audience, pain points, desires, and social proof.</p>
            </div>
            <Button 
              onClick={saveChanges} 
              disabled={isSaving}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 h-auto"
              size="lg"
            >
              {isSaving ? (
                <>
                  <Loader className="h-5 w-5 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-5 w-5 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto py-6 px-4 sm:px-6">
        <div className="grid gap-6">
          {/* Target Audience */}
          <div className="bg-white dark:bg-zinc-800 rounded-lg border border-zinc-200 dark:border-zinc-700 overflow-hidden">
            <div className="border-b border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/80 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center">
                <Users className="h-4 w-4 text-blue-500 dark:text-blue-400 mr-2" />
                <h2 className="text-lg font-medium text-zinc-900 dark:text-white">Target Audience Segments</h2>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowAddAudience(!showAddAudience)}
                className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Segment
              </Button>
            </div>
            <div className="p-4">
              {showAddAudience && (
                <div className="mb-4 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800/30">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-xs font-medium text-blue-700 dark:text-blue-300">Add New Audience Segment</h3>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setShowAddAudience(false)}
                      className="h-6 w-6 p-0 text-blue-700 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-zinc-700 dark:text-zinc-200 mb-1 block">Segment Name</label>
                      <Input 
                        value={newAudienceSegment.segment}
                        onChange={(e) => setNewAudienceSegment({...newAudienceSegment, segment: e.target.value})}
                        placeholder="e.g., Small Business Owners"
                        className="border-blue-200 dark:border-blue-800 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-600 focus:outline-none dark:bg-zinc-800 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-zinc-700 dark:text-zinc-200 mb-1 block">Description</label>
                      <Textarea 
                        value={newAudienceSegment.description}
                        onChange={(e) => setNewAudienceSegment({...newAudienceSegment, description: e.target.value})}
                        placeholder="Describe this audience segment..."
                        className="border-blue-200 dark:border-blue-800 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-600 focus:outline-none dark:bg-zinc-800 dark:text-white"
                        rows={3}
                      />
                    </div>
                    <div className="flex justify-end">
                      <Button 
                        onClick={handleAddAudienceSegment}
                        disabled={!newAudienceSegment.segment.trim() || !newAudienceSegment.description.trim()}
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 text-white transition-colors"
                      >
                        Add Segment
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              {results.targetAudience && results.targetAudience.length > 0 ? (
                <div className="grid md:grid-cols-2 gap-4">
                  {results.targetAudience.map((segment: any, index: number) => (
                    <div key={index} className="bg-zinc-50 dark:bg-zinc-700/50 rounded-lg p-4 border border-zinc-200 dark:border-zinc-600 group relative hover:shadow-sm dark:hover:shadow-zinc-900/30">
                      <button 
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-full bg-blue-100 dark:bg-blue-800/50 text-blue-500 dark:text-blue-300 hover:text-blue-600 dark:hover:text-blue-200"
                        onClick={() => handleRemoveItem('targetAudience', index)}
                      >
                        <X className="h-3 w-3" />
                      </button>
                      <h3 className="text-sm font-medium text-blue-600 dark:text-blue-300 mb-2 flex items-center">
                        <span className="inline-block w-2 h-2 bg-blue-500 dark:bg-blue-400 rounded-full mr-2"></span>
                        {segment.segment}
                      </h3>
                      <p className="text-sm text-zinc-700 dark:text-zinc-200 leading-relaxed">{segment.description}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-zinc-500 dark:text-zinc-400 text-center py-4">No target audience data available.</p>
              )}
            </div>
          </div>

          {/* Pain Points */}
          <div className="bg-white dark:bg-zinc-800 rounded-lg border border-zinc-200 dark:border-zinc-700 overflow-hidden">
            <div className="border-b border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/80 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center">
                <Target className="h-4 w-4 text-blue-500 dark:text-blue-400 mr-2" />
                <h2 className="text-lg font-medium text-zinc-900 dark:text-white">Pain Points</h2>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowAddPainPoint(!showAddPainPoint)}
                className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Pain Point
              </Button>
            </div>
            <div className="p-4">
              {showAddPainPoint && (
                <div className="mb-4 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800/30">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-xs font-medium text-blue-700 dark:text-blue-300">Add New Pain Point</h3>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setShowAddPainPoint(false)}
                      className="h-6 w-6 p-0 text-blue-700 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-zinc-700 dark:text-zinc-200 mb-1 block">Problem</label>
                      <Textarea 
                        value={newPainPoint.problem}
                        onChange={(e) => setNewPainPoint({problem: e.target.value})}
                        placeholder="Describe the pain point..."
                        className="border-blue-200 dark:border-blue-800 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-600 focus:outline-none dark:bg-zinc-800 dark:text-white"
                        rows={2}
                      />
                    </div>
                    <div className="flex justify-end">
                      <Button 
                        onClick={handleAddPainPoint}
                        disabled={!newPainPoint.problem.trim()}
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 text-white transition-colors"
                      >
                        Add Pain Point
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              {results.painPoints && results.painPoints.length > 0 ? (
                <div className="grid md:grid-cols-2 gap-4">
                  {results.painPoints.map((item: any, index: number) => (
                    <div key={index} className="bg-zinc-50 dark:bg-zinc-700/50 rounded-lg p-4 border border-zinc-200 dark:border-zinc-600 flex items-start gap-3 group relative hover:shadow-sm dark:hover:shadow-zinc-900/30">
                      <button 
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-full bg-blue-100 dark:bg-blue-800/50 text-blue-500 dark:text-blue-300 hover:text-blue-600 dark:hover:text-blue-200"
                        onClick={() => handleRemoveItem('painPoints', index)}
                      >
                        <X className="h-3 w-3" />
                      </button>
                      <div className="bg-blue-100 dark:bg-blue-800/50 rounded-md p-1.5 mt-0.5 flex items-center justify-center">
                        <span className="h-1.5 w-1.5 bg-blue-500 dark:bg-blue-400 rounded-full"></span>
                      </div>
                      <p className="text-sm text-zinc-700 dark:text-zinc-200 leading-relaxed">{item.problem}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-zinc-500 dark:text-zinc-400 text-center py-4">No pain points data available.</p>
              )}
            </div>
          </div>

          {/* Customer Desires */}
          <div className="bg-white dark:bg-zinc-800 rounded-lg border border-zinc-200 dark:border-zinc-700 overflow-hidden">
            <div className="border-b border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/80 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center">
                <Heart className="h-4 w-4 text-blue-500 dark:text-blue-400 mr-2" />
                <h2 className="text-lg font-medium text-zinc-900 dark:text-white">Customer Desires</h2>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowAddDesire(!showAddDesire)}
                className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Desire
              </Button>
            </div>
            <div className="p-4">
              {showAddDesire && (
                <div className="mb-4 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800/30">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-xs font-medium text-blue-700 dark:text-blue-300">Add New Customer Desire</h3>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setShowAddDesire(false)}
                      className="h-6 w-6 p-0 text-blue-700 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-zinc-700 dark:text-zinc-200 mb-1 block">Desire</label>
                      <Textarea 
                        value={newDesire.desire}
                        onChange={(e) => setNewDesire({desire: e.target.value})}
                        placeholder="Describe what customers want..."
                        className="border-blue-200 dark:border-blue-800 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-600 focus:outline-none dark:bg-zinc-800 dark:text-white"
                        rows={2}
                      />
                    </div>
                    <div className="flex justify-end">
                      <Button 
                        onClick={handleAddDesire}
                        disabled={!newDesire.desire.trim()}
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 text-white transition-colors"
                      >
                        Add Desire
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              {results.desires && results.desires.length > 0 ? (
                <div className="grid md:grid-cols-2 gap-4">
                  {results.desires.map((item: any, index: number) => (
                    <div key={index} className="bg-zinc-50 dark:bg-zinc-700/50 rounded-lg p-4 border border-zinc-200 dark:border-zinc-600 flex items-start gap-3 group relative hover:shadow-sm dark:hover:shadow-zinc-900/30">
                      <button 
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-full bg-blue-100 dark:bg-blue-800/50 text-blue-500 dark:text-blue-300 hover:text-blue-600 dark:hover:text-blue-200"
                        onClick={() => handleRemoveItem('desires', index)}
                      >
                        <X className="h-3 w-3" />
                      </button>
                      <div className="bg-cyan-100 dark:bg-cyan-900/50 rounded-md p-1.5 mt-0.5 flex items-center justify-center">
                        <span className="h-1.5 w-1.5 bg-cyan-500 dark:bg-cyan-400 rounded-full"></span>
                      </div>
                      <p className="text-sm text-zinc-700 dark:text-zinc-200 leading-relaxed">{item.desire}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-zinc-500 dark:text-zinc-400 text-center py-4">No customer desires data available.</p>
              )}
            </div>
          </div>

          {/* Social Proof */}
          <div className="bg-white dark:bg-zinc-800 rounded-lg border border-zinc-200 dark:border-zinc-700 overflow-hidden">
            <div className="border-b border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/80 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center">
                <MessageSquare className="h-4 w-4 text-blue-500 dark:text-blue-400 mr-2" />
                <h2 className="text-lg font-medium text-zinc-900 dark:text-white">Social Proof & Testimonials</h2>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowAddSocialProof(!showAddSocialProof)}
                className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Testimonial
              </Button>
            </div>
            <div className="p-4">
              {showAddSocialProof && (
                <div className="mb-4 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800/30">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-xs font-medium text-blue-700 dark:text-blue-300">Add New Testimonial</h3>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setShowAddSocialProof(false)}
                      className="h-6 w-6 p-0 text-blue-700 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-zinc-700 dark:text-zinc-200 mb-1 block">Testimonial</label>
                      <Textarea 
                        value={newSocialProof.testimonial}
                        onChange={(e) => setNewSocialProof({...newSocialProof, testimonial: e.target.value})}
                        placeholder="Enter the testimonial text..."
                        className="border-blue-200 dark:border-blue-800 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-600 focus:outline-none dark:bg-zinc-800 dark:text-white"
                        rows={3}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-sm font-medium text-zinc-700 dark:text-zinc-200 mb-1 block">Author</label>
                        <Input 
                          value={newSocialProof.author}
                          onChange={(e) => setNewSocialProof({...newSocialProof, author: e.target.value})}
                          placeholder="e.g., John Doe, CEO"
                          className="border-blue-200 dark:border-blue-800 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-600 focus:outline-none dark:bg-zinc-800 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-zinc-700 dark:text-zinc-200 mb-1 block">Theme (Optional)</label>
                        <Input 
                          value={newSocialProof.theme}
                          onChange={(e) => setNewSocialProof({...newSocialProof, theme: e.target.value})}
                          placeholder="e.g., Reliability"
                          className="border-blue-200 dark:border-blue-800 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-600 focus:outline-none dark:bg-zinc-800 dark:text-white"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <Button 
                        onClick={handleAddSocialProof}
                        disabled={!newSocialProof.testimonial.trim() || !newSocialProof.author.trim()}
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 text-white transition-colors"
                      >
                        Add Testimonial
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              {results.socialProof && results.socialProof.length > 0 ? (
                <div className="grid md:grid-cols-2 gap-6">
                  {results.socialProof.map((item: any, index: number) => (
                    <div key={index} className="bg-zinc-50 dark:bg-zinc-700/50 rounded-lg p-6 border border-zinc-200 dark:border-zinc-600 group relative hover:shadow-sm dark:hover:shadow-zinc-900/30">
                      <button 
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-full bg-blue-100 dark:bg-blue-800/50 text-blue-500 dark:text-blue-300 hover:text-blue-600 dark:hover:text-blue-200"
                        onClick={() => handleRemoveItem('socialProof', index)}
                      >
                        <X className="h-3 w-3" />
                      </button>
                      <div className="mb-4 relative">
                        <div className="absolute -top-1 -left-2 text-blue-300 dark:text-blue-500 opacity-20 text-4xl font-serif">"</div>
                        <p className="text-zinc-700 dark:text-zinc-200 italic relative z-10">"{item.testimonial}"</p>
                        <div className="absolute -bottom-1 -right-2 text-blue-300 dark:text-blue-500 opacity-20 text-4xl font-serif">"</div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-blue-600 dark:text-blue-300">{item.author}</span>
                        {item.theme && (
                          <span className="text-xs bg-blue-100 dark:bg-blue-800/50 text-blue-600 dark:text-blue-300 px-2 py-1 rounded-full">
                            {item.theme}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-zinc-500 dark:text-zinc-400 text-center py-4">No social proof data available.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudiencePage; 